import React, { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchUserData, addToCart } from '../redux/slice/user.slice';
import { fetchGames } from '../redux/slice/game.slice';
import {
  Search, Grid, List, ChevronRight, ArrowLeft, X
} from 'lucide-react';
import { FaWindows, FaApple, FaXbox } from 'react-icons/fa';
import { DiAndroid } from 'react-icons/di';
import { SiOculus, SiPlaystation, SiSteamdeck } from 'react-icons/si';
import { BsNintendoSwitch } from 'react-icons/bs';
import { TbDeviceVisionProFilled } from 'react-icons/tb';
import gsap from 'gsap';
import { useNavigate } from 'react-router-dom';
// import { getGamePath } from '../utils/gamePath';
// import LazyImage from '../components/common/LazyImage';
import GameCard, { GameCardSkeleton } from '../components/common/GameCard';
import GameListCard, { GameListCardSkeleton } from '../components/common/GameListCard';
import astronautImg from '../assets/images/wishlist.webp';
import { useGridCorners } from '../hooks/useGridCorners';

const PlatformIcon = {
  windows: FaWindows,
  playstation: SiPlaystation,
  xbox: FaXbox,
  nintendo: BsNintendoSwitch,
  quest: SiOculus,
  android: DiAndroid,
  ios: FaApple,
  visionpro: TbDeviceVisionProFilled,
  steamdeck: SiSteamdeck
};

const PlatformColor = {
  windows: '#0078d7',
  playstation: '#00439c',
  xbox: '#107c10',
  nintendo: '#e60012',
  quest: '#ffffff',
  android: '#3ddc84',
  ios: '#a3aaae',
  visionpro: '#a855f7',
  steamdeck: '#38bdf8'
};

const PlatformLabel = {
  windows: 'Windows PC',
  playstation: 'PlayStation',
  xbox: 'Xbox',
  nintendo: 'Nintendo Switch',
  quest: 'Meta Quest VR',
  android: 'Android',
  ios: 'iOS',
  visionpro: 'Vision Pro',
  steamdeck: 'Steam Deck'
};


export default function Wishlist() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const containerRef = useRef(null);

  // Redux state
  const { user, isAuthenticated } = useSelector((s) => s.auth);
  const { wishlist, wishlistDetailed } = useSelector((s) => s.userSlice);
  const { games } = useSelector((s) => s.game);

  const [initialLoading, setInitialLoading] = useState(() => {
    if (!isAuthenticated) return false;
    if (wishlistDetailed.length > 0) return false;
    return wishlist.length > 0;
  });

  useEffect(() => {
    let isMounted = true;
    const loadWishlistData = async () => {
      try {
        const userId = user?._id || user?.id;
        if (isAuthenticated && userId) {
          await dispatch(fetchUserData(userId));
        }
      } catch (err) {
        console.error("Wishlist load error:", err);
      } finally {
        if (isMounted) {
          setInitialLoading(false);
        }
      }
    };

    if (isAuthenticated && wishlistDetailed.length === 0) {
      loadWishlistData();
    } else {
      setInitialLoading(false);
    }

    return () => { isMounted = false; };
  }, [dispatch, isAuthenticated, user?._id, user?.id, wishlistDetailed.length]);

  useEffect(() => {
    if (!games || games.length === 0) {
      dispatch(fetchGames());
    }
  }, [dispatch, games]);

  const isLoading = initialLoading || (isAuthenticated && wishlist.length > 0 && wishlistDetailed.length === 0);

  // Map all wishlist IDs to detailed objects or cached games so newly added items are never dropped
  const mappedItems = wishlist.map(id => {
    const rawId = id?._id || id?.id || id;
    const fromDetailed = wishlistDetailed.find(g => (g._id || g.id) === rawId);
    if (fromDetailed) return fromDetailed;
    const fromGames = games.find(g => (g._id || g.id) === rawId);
    if (fromGames) return fromGames;
    return null;
  }).filter(Boolean);

  const items = mappedItems.length > 0
    ? mappedItems
    : (wishlistDetailed.length > 0 ? wishlistDetailed : games.filter(g => wishlist.some(w => (w?._id || w?.id || w) === (g._id || g.id))));

  // Local component state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [sortBy, setSortBy] = useState('newest');
  const [viewMode, setViewMode] = useState('grid');

  const { gridCols, setGridRef, getCornerStyle } = useGridCorners({ viewMode });

  // Auto-switch to grid view on small screens (< 601px) matching AllGames.jsx
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 601) {
        setViewMode('grid');
      }
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Platform selector state
  const [platformSelectorGame, setPlatformSelectorGame] = useState(null);
  const [selectedPlatformForGame, setSelectedPlatformForGame] = useState('');

  // Extract unique categories from wishlist items for filtering
  const categories = ['All', ...new Set(items.map(item => item.category?.name).filter(Boolean))];

  // Apply search, category filtering, and sorting
  const filteredItems = items.filter(item => {
    const matchesSearch = item.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.description?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesCategory = selectedCategory === 'All' || item.category?.name === selectedCategory;

    return matchesSearch && matchesCategory;
  }).sort((a, b) => {
    if (sortBy === 'price-low') {
      const getPrice = (g) => {
        const plat = Object.values(g.platforms || {}).find(p => p.available);
        return g.platforms?.windows?.available ? g.platforms.windows.price : (plat ? plat.price : g.price || 0);
      };
      return getPrice(a) - getPrice(b);
    }
    if (sortBy === 'price-high') {
      const getPrice = (g) => {
        const plat = Object.values(g.platforms || {}).find(p => p.available);
        return g.platforms?.windows?.available ? g.platforms.windows.price : (plat ? plat.price : g.price || 0);
      };
      return getPrice(b) - getPrice(a);
    }
    if (sortBy === 'rating') {
      return (b.rating || 5) - (a.rating || 5);
    }
    if (sortBy === 'name') {
      return a.title?.localeCompare(b.title);
    }
    // Default or Newest: assuming items array maintains relative order or has date
    return 0;
  });

  // Entry animations (run only once on page mount)
  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo('.wishlist-animate',
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.8, stagger: 0.05, ease: 'power3.out', clearProps: 'transform,opacity' }
      );
    }, containerRef);
    return () => ctx.revert();
  }, []);

  const handleAddToCartClick = (game) => {
    const available = Object.entries(game.platforms || {}).filter(([_, info]) => info?.available);
    if (available.length === 1) {
      dispatch(addToCart({ ...game, selectedPlatform: available[0][0] }));
    } else if (available.length > 1) {
      setPlatformSelectorGame(game);
      const keys = available.map(a => a[0]);
      setSelectedPlatformForGame(keys.includes('windows') ? 'windows' : keys[0]);
    } else {
      dispatch(addToCart({ ...game, selectedPlatform: 'windows' }));
    }
  };

  return (
    <div ref={containerRef} className="min-h-screen bg-transparent pt-24 pb-12 sm:pt-32 sm:pb-32 relative overflow-hidden flex flex-col items-center">
      {/* Dynamic Ambient Platform Lighting with smooth feathered fade */}
      <div
        className="absolute inset-0 pointer-events-none overflow-hidden"
        style={{
          maskImage: 'linear-gradient(to bottom, black 75%, transparent 100%)',
          WebkitMaskImage: 'linear-gradient(to bottom, black 75%, transparent 100%)'
        }}
      >
        <div className="absolute top-0 left-0 w-full h-full bg-cyber-grid opacity-[0.04]" />
        <div
          className="absolute top-[8%] left-1/2 -translate-x-1/2 w-[70vw] max-w-[900px] h-[400px] rounded-full blur-[140px] transition-all duration-700 opacity-20"
          style={{ backgroundColor: '#00f0ff' }}
        />
        <div className="absolute top-[30%] right-[-10%] w-[45vw] h-[45vw] max-w-[600px] bg-cyber-purple/10 rounded-full blur-[150px]" />
        <div className="absolute bottom-[10%] left-[-10%] w-[45vw] h-[45vw] max-w-[600px] bg-cyber-blue/10 rounded-full blur-[150px]" />
      </div>

      <div className="container w-full relative z-10 mx-auto">
        {/* Page Header */}
        <div className="wishlist-animate mb-4 md600:mb-6 flex flex-col md600:flex-row md600:items-end justify-between gap-4 sm:gap-6 relative z-10">
          <div className="flex-shrink-0">
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center justify-center gap-2 mb-4 px-3.5 sm:px-4 py-2 sm:py-2.5 rounded-xl bg-[#151a26] border border-white/[0.08] text-xs font-cyber font-bold text-gray-200 hover:text-white hover:border-white/20 hover:bg-[#1a2130] transition-all duration-300 uppercase tracking-wider group/btn shadow-sm"
            >
              <ArrowLeft className="w-3.5 h-3.5 group-hover/btn:-translate-x-1 transition-transform" />
              <span>BACK TO HOME</span>
            </button>
            <p className="text-[10px] font-cyber font-semibold text-cyber-cyan uppercase tracking-[0.25em] mb-2 flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-cyber-cyan shadow-[0_0_6px_#00f0ff] animate-pulse"></span>
              Vault Storage
            </p>
            <h1 className="font-cyber font-semibold text-3xl sm:text-4xl lg:text-5xl text-white uppercase tracking-tight whitespace-nowrap">
              My <span className="animate-gradient-text drop-shadow-[0_0_20px_rgba(0,240,255,0.35)]">Wishlist</span>
            </h1>
            <div className="h-[2px] w-20 animate-gradient-bg mt-3 rounded-full"></div>
          </div>

          {items.length > 0 && (
            <div className="flex flex-col sm500:mb-4 md600:flex-row items-stretch sm:items-center gap-3 w-full md600:w-auto md600:flex-shrink-0">
              {/* Search */}
              <div className="relative group w-full md600:w-48 flex-shrink-0">
                <div className="relative flex items-center w-full rounded-2xl bg-[#0c101b]/90 backdrop-blur-2xl border border-white/[0.1] transition-all duration-300 hover:border-white/20 focus-within:border-cyber-cyan focus-within:shadow-[0_0_25px_rgba(0,240,255,0.25)]">
                  <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 group-focus-within:text-cyber-cyan group-focus-within:drop-shadow-[0_0_8px_rgba(0,240,255,0.6)] transition-colors duration-300 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="Search wishlist..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 pr-9 py-2.5 sm:py-3 text-xs font-cyber text-white placeholder-gray-500 bg-transparent focus:outline-none w-full tracking-wider"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
                      title="Clear search"
                      aria-label="Clear search"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>

              {/* View switches */}
              <div className="hidden md600:flex items-center gap-0.5 sm:gap-1 p-0.5 sm:p-1 bg-[#151a26] rounded-xl border border-white/[0.08] flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setViewMode('grid')}
                  className={`p-1.5 sm:p-2 rounded-lg transition-all duration-300 ${viewMode === 'grid'
                    ? 'bg-white text-black shadow-md shadow-white/10'
                    : 'text-gray-400 hover:text-white'
                    }`}
                  title="Grid View"
                  aria-label="Grid View"
                >
                  <Grid className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('list')}
                  className={`p-1.5 sm:p-2 rounded-lg transition-all duration-300 ${viewMode === 'list'
                    ? 'bg-white text-black shadow-md shadow-white/10'
                    : 'text-gray-400 hover:text-white'
                    }`}
                  title="List View"
                  aria-label="List View"
                >
                  <List className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Custom Floating Astronaut Animation Style */}
        <style>{`
                    @keyframes astronautFloat {
                        0%, 100% { transform: translateY(0px) rotate(0deg); }
                        50% { transform: translateY(-14px) rotate(1.8deg); }
                    }
                    .animate-astronaut-float {
                        animation: astronautFloat 3.8s ease-in-out infinite;
                    }
                `}</style>

        {isLoading ? (
          <div>
            <div
              ref={setGridRef}
              className={viewMode === 'grid'
                ? 'grid grid-cols-1 sm500:grid-cols-2 md:grid-cols-3 2xl:grid-cols-4 gap-4 sm:gap-6 lg:gap-8 w-full'
                : 'grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 w-full'}>
              {[...Array(8)].map((_, i) => (
                viewMode === 'grid' ? (
                  <GameCardSkeleton
                    key={i}
                    style={getCornerStyle(i, 8, gridCols)}
                  />
                ) : (
                  <GameListCardSkeleton
                    key={i}
                    style={getCornerStyle(i, 8, gridCols)}
                  />
                )
              ))}
            </div>
          </div>
        ) : items.length === 0 ? (
          /* Premium Empty State with Astronaut Illustration UI */
          <div className="wishlist-animate flex flex-col items-center justify-center py-8 sm:py-12 text-center max-w-lg mx-auto space-y-4 px-4">
            <div className="relative group w-full flex items-center justify-center p-1 sm:p-2">
              {/* Dark Teal Radial Glow Backdrop matching screenshot */}
              <div
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-60 xs:w-68 sm:w-80 h-60 xs:h-68 sm:h-80 rounded-full blur-2xl pointer-events-none opacity-90"
              />
              <img
                src={astronautImg}
                alt="Wishlist Empty Astronaut"
                className="w-36 xs:w-44 sm:w-52 md:w-56 max-w-[240px] max-h-[250px] h-auto object-contain relative z-10 animate-astronaut-float cursor-pointer transition-transform duration-500 group-hover:scale-105"
              />
            </div>

            <div className="space-y-2.5 max-w-md">
              <h2 className="font-cyber font-black text-2xl md600:text-4xl text-[#00f0ff] uppercase tracking-wider drop-shadow-[0_0_18px_rgba(0,240,255,0.4)]">
                WISHLIST EMPTY
              </h2>
              <p className="text-xs md600:text-sm text-gray-300 font-sans leading-relaxed">
                Looks like your game list is empty! Check out our database and mark your favorite titles for easy licensing access.
              </p>
            </div>

            <button
              onClick={() => navigate('/all-games')}
              className="w-full sm:w-auto relative overflow-hidden inline-flex items-center justify-center gap-2.5 px-6 py-2.5 sm:px-7 sm:py-3 rounded-xl bg-gradient-to-r from-[#00f0ff] via-[#5c60ff] to-[#bd00ff] text-black font-cyber font-bold text-xs sm:text-[13px] uppercase tracking-wider hover:scale-105 active:scale-95 transition-all duration-300 whitespace-nowrap group before:absolute before:top-0 before:-left-[100%] before:w-full before:h-full before:bg-gradient-to-r before:from-transparent before:via-white/40 before:to-transparent before:transition-all before:duration-700 hover:before:left-[100%]"
            >
              <span>DISCOVER GAMES</span>
              <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        ) : (
          <div>
            {filteredItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <Search className="w-10 h-10 text-gray-700 mb-3 animate-bounce" />
                <h3 className="text-md font-cyber font-semibold text-white uppercase">No items match</h3>
                <p className="text-gray-500 text-xs font-sans">Modify your query or select a different category filter.</p>
              </div>
            ) : (
              <div
                ref={setGridRef}
                className={viewMode === 'grid'
                  ? 'grid grid-cols-1 sm500:grid-cols-2 md:grid-cols-3 2xl:grid-cols-4 gap-4 sm:gap-6 lg:gap-8 w-full'
                  : 'grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 w-full'}>
                {filteredItems.map((game, index) => (
                  viewMode === 'grid' ? (
                    <GameCard
                      key={game.id || game._id}
                      game={game}
                      onAddToCart={handleAddToCartClick}
                      isWishlistPage={true}
                      style={getCornerStyle(index, filteredItems.length, gridCols)}
                    />
                  ) : (
                    <GameListCard
                      key={game.id || game._id}
                      game={game}
                      onAddToCart={handleAddToCartClick}
                      isWishlistPage={true}
                      style={getCornerStyle(index, filteredItems.length, gridCols)}
                    />
                  )
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Platform Selector Modal */}
      {platformSelectorGame && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-fade-in">
          <div className="w-full max-w-md bg-[#0c0c18] border border-cyber-cyan/30 rounded-[1.5rem] sm:rounded-[2.5rem] p-5 sm:p-8 shadow-[0_0_30px_rgba(0,240,255,0.15)] relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-cyber-cyan via-cyber-purple to-cyber-pink"></div>

            <div className="space-y-6">
              <div className="text-center space-y-2">
                <h3 className="text-xl font-cyber font-black text-white uppercase tracking-tight">Select Platform</h3>
                <p className="text-gray-400 text-xs font-medium">Choose your target platform for <span className="text-cyber-cyan">{platformSelectorGame.title}</span></p>
              </div>

              <div className="space-y-2.5 max-h-60 overflow-y-auto pr-1">
                {Object.entries(platformSelectorGame.platforms || {})
                  .filter(([_, info]) => info?.available)
                  .map(([key, info]) => {
                    const isSelected = selectedPlatformForGame === key;
                    const Icon = PlatformIcon[key] || FaWindows;
                    const label = PlatformLabel[key] || key;
                    const brandColor = PlatformColor[key] || '#9ca3af';
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setSelectedPlatformForGame(key)}
                        className={`w-full flex items-center justify-between p-4 rounded-xl border transition-all text-left
                          ${isSelected
                            ? 'bg-cyber-cyan/10 border-cyber-cyan text-white shadow-[0_0_15px_rgba(0,240,255,0.1)]'
                            : 'bg-white/[0.02] border-white/5 text-gray-400 hover:text-white hover:bg-white/[0.04]'}`}
                      >
                        <div className="flex items-center gap-3">
                          <Icon className="w-4 h-4" style={{ color: brandColor }} />
                          <span className="text-xs font-cyber font-bold uppercase tracking-wide">{label}</span>
                        </div>
                        <span className="text-xs font-mono font-bold text-white">
                          {info.price === 0 ? 'FREE' : `$${info.price}`}
                        </span>
                      </button>
                    );
                  })}
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => { setPlatformSelectorGame(null); setSelectedPlatformForGame(''); }}
                  className="flex-1 py-3.5 rounded-2xl bg-white/5 border border-white/10 text-gray-400 font-cyber font-bold text-[10px] uppercase tracking-widest hover:bg-white/10 hover:text-white transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    dispatch(addToCart({ ...platformSelectorGame, selectedPlatform: selectedPlatformForGame }));
                    setPlatformSelectorGame(null);
                    setSelectedPlatformForGame('');
                  }}
                  disabled={!selectedPlatformForGame}
                  className="flex-1 py-3.5 rounded-2xl bg-cyber-cyan/20 border border-cyber-cyan/40 text-cyber-cyan font-cyber font-bold text-[10px] uppercase tracking-widest hover:bg-cyber-cyan/30 disabled:opacity-40 transition-all"
                >
                  Add to Cart
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
